import { STOCK_UI } from '@diamond/shared/decimal-util-web';
import { VOUCHER_REWARD_TYPE } from '@diamond/shared/types';
import { dinero, toDecimal } from 'dinero.js';

import { ProductWithPromo } from '../promos/promos';
import {
  ProcessedVoucherData,
  processVoucherData,
} from '../promos/voucher-processing';
import { resolveOrderDetailValue } from './resolve-order-detail-value';

type OrderDetailProductDiffFields = {
  is_stock_diff?: boolean;
  is_price_diff?: boolean;
  subtotal_diff?: number;
  original_subtotal?: number;
  subtotal?: number;
};

export function isOrderDetailChangePriceStock(
  product: OrderDetailProductDiffFields
): boolean {
  return (
    Boolean(product.is_stock_diff) ||
    (Boolean(product.is_price_diff) &&
      (product.subtotal_diff ?? 0) !== 0 &&
      (product.original_subtotal ?? 0) !== (product.subtotal ?? 0))
  );
}

const toDisplayQuantity = (magnifiedQuantity: number | undefined): number =>
  Number(
    toDecimal(
      dinero({
        amount: Number(magnifiedQuantity ?? 0),
        currency: STOCK_UI,
      })
    )
  );

const resolveOrderDetailUnitPrice = (
  product: ProductWithPromo,
  voucherData: ProcessedVoucherData
): number => {
  const latestPrice = product.latest_price ?? 0;

  if (voucherData.hasVoucherDiscount && voucherData.voucherDiscount) {
    const rewardType = voucherData.voucherDiscount.reward_type;

    if (
      rewardType === VOUCHER_REWARD_TYPE.DISCOUNT_AMOUNT &&
      voucherData.discountAmount !== null
    ) {
      return Math.max(0, latestPrice - voucherData.discountAmount);
    }

    if (
      rewardType === VOUCHER_REWARD_TYPE.DISCOUNT_PERCENT &&
      voucherData.discountAmount !== null
    ) {
      const voucherQuantity = toDisplayQuantity(
        voucherData.voucherDiscount.quantity
      );
      const perUnitDiscount =
        voucherQuantity > 0 ? voucherData.discountAmount / voucherQuantity : 0;

      return Math.max(0, latestPrice - perUnitDiscount);
    }

    if (voucherData.finalUnitPrice !== null) {
      return voucherData.finalUnitPrice;
    }
  }

  const discountPromo = product.hasDiscount
    ? product.compiled_discount?.find((promo) => promo.is_discount)
    : null;

  if (discountPromo?.price_after_discount != null) {
    return discountPromo.price_after_discount;
  }

  return latestPrice;
};

/**
 * Line total from API — not unitPrice × confirmedQuantity.
 * Always uses product.subtotal, including voucher rows (not final_subtotal).
 */
const resolveOrderDetailTotalPrice = (product: ProductWithPromo): number => {
  if (product.is_not_processed) {
    return 0;
  }

  return product.subtotal ?? 0;
};

export type OrderDetailProductDisplay = {
  originalUnitPrice?: number;
  unitPrice: number;
  totalPrice: number;
  initialUnitPrice: number;
  initialTotalPrice: number;
  quantity: number;
  confirmedQuantity: number;
  showPriceSection: boolean;
  isChangePriceStock: boolean;
  showInitialUnitPrice: boolean;
  showInitialTotalPrice: boolean;
  showInitialQuantity: boolean;
};

function getCurrentOriginalUnitPrice(
  product: ProductWithPromo
): number | undefined {
  const hasCurrentOriginalUnitPrice = Boolean(
    product.latest_price || product.price
  );

  if (!hasCurrentOriginalUnitPrice) {
    return undefined;
  }

  if (product.latest_price !== null && product.latest_price !== undefined) {
    return product.latest_price;
  }

  return product.price;
}

function getCurrentOrderDetailProductDisplay(
  product: ProductWithPromo
): OrderDetailProductDisplay {
  const voucherData = processVoucherData(product);
  const quantity = toDisplayQuantity(product.quantity);
  const confirmedQuantity = toDisplayQuantity(product.confirmed_quantity);
  const unitPrice = resolveOrderDetailUnitPrice(product, voucherData);
  const totalPrice = resolveOrderDetailTotalPrice(product);
  const initialUnitPrice = product.price ?? 0;
  const initialTotalPrice = product.original_subtotal ?? 0;

  const isChangePriceStock = isOrderDetailChangePriceStock(product);
  const latestPrice = product.latest_price ?? 0;
  const isStockDiff = product.is_stock_diff ?? false;

  return {
    originalUnitPrice: getCurrentOriginalUnitPrice(product),
    unitPrice,
    totalPrice,
    initialUnitPrice,
    initialTotalPrice,
    quantity,
    confirmedQuantity,
    showPriceSection: confirmedQuantity > 0,
    isChangePriceStock,
    showInitialUnitPrice:
      isChangePriceStock && initialUnitPrice !== latestPrice,
    showInitialTotalPrice: isChangePriceStock,
    showInitialQuantity: isStockDiff && quantity !== confirmedQuantity,
  };
}

function getPoDetailsProductDisplay(
  product: ProductWithPromo,
  currentDisplay: OrderDetailProductDisplay
): OrderDetailProductDisplay {
  const checkoutQuantity = resolveOrderDetailValue(
    product.checkout_quantity,
    product.quantity
  );
  const latestQuantity = resolveOrderDetailValue(
    product.latest_quantity,
    product.confirmed_quantity
  );
  const checkoutUnitPrice = resolveOrderDetailValue(
    product.checkout_unit_price,
    currentDisplay.initialUnitPrice
  );
  const latestUnitPrice = resolveOrderDetailValue(
    product.latest_unit_price,
    currentDisplay.unitPrice
  );
  const checkoutSubtotal = resolveOrderDetailValue(
    product.checkout_subtotal,
    currentDisplay.initialTotalPrice
  );
  const latestSubtotal = resolveOrderDetailValue(
    product.latest_subtotal,
    currentDisplay.totalPrice
  );
  const isStockDiff = product.is_stock_diff === true;
  const isPriceDiff = product.is_price_diff === true;
  const showInitialQuantity =
    isStockDiff && checkoutQuantity !== latestQuantity;
  const showInitialUnitPrice =
    isPriceDiff && checkoutUnitPrice !== latestUnitPrice;
  const showInitialTotalPrice =
    (isStockDiff || isPriceDiff) && checkoutSubtotal !== latestSubtotal;
  const isChangePriceStock =
    showInitialQuantity || showInitialUnitPrice || showInitialTotalPrice;
  const confirmedQuantity = toDisplayQuantity(latestQuantity);

  return {
    ...currentDisplay,
    quantity: toDisplayQuantity(checkoutQuantity),
    confirmedQuantity,
    originalUnitPrice: resolveOrderDetailValue(
      product.latest_original_unit_price,
      currentDisplay.originalUnitPrice
    ),
    unitPrice: latestUnitPrice,
    initialUnitPrice: checkoutUnitPrice,
    totalPrice: latestSubtotal,
    initialTotalPrice: checkoutSubtotal,
    showPriceSection: confirmedQuantity > 0,
    isChangePriceStock,
    showInitialUnitPrice,
    showInitialTotalPrice,
    showInitialQuantity,
  };
}

export function getOrderDetailProductDisplay(
  product: ProductWithPromo,
  poDetailsDataEnabled = false
): OrderDetailProductDisplay {
  const currentDisplay = getCurrentOrderDetailProductDisplay(product);

  if (!poDetailsDataEnabled) {
    return currentDisplay;
  }

  return getPoDetailsProductDisplay(product, currentDisplay);
}
